import type { RequestHandler } from 'msw';

import { DemoProfile } from '../types';
import { resolveStateCode } from '../stateTax';

import {
    buildAnalyticsPassthroughHandler,
    buildCatchAllHandler,
    buildFallbackHandlers,
    buildOverrideHandlers,
} from './overrides';
import buildMemberTaskHandlers from './memberTasks';
import buildQuestionnaireHandlers from './questionnaire';
import buildPayrollHandlers from './payroll';
import storybookHandlerMap from './fromStorybook';

/**
 * Demo handler set, ordered by priority (first match wins):
 *   1. Analytics passthrough — `/trackevents/` is the one call allowed out to the real backend.
 *   2. Member tasks — the whole `/v2/members/tasks/*` surface (see memberTasks.ts). First so the
 *      ranked `/feed/` route can't be swallowed by a looser task pattern.
 *   3. Overrides — identity + per-profile content the demo specifically cares about.
 *   4. Payroll — the dashboard's `/api/payroll/v1/*` surface (see payroll.ts).
 *   5. Storybook baseline — 14 handler groups the team already maintains for stories.
 *   6. Fallbacks — neutral GET=[] / write=ok responses for the Django origin.
 *   7. Catch-all — passthrough same-origin assets, 204 everything else. Belt-and-suspenders
 *      so un-stubbed cross-origin calls (Stripe, Datadog, Segment, etc.) never hit the
 *      real network from inside a demo session.
 *
 * The fallback layer prevents any request to the configured Django host from 401-ing, which
 * is critical because collectiveApi's 401 interceptor would otherwise force-logout the visitor.
 */
export default function buildHandlers(profile: DemoProfile): RequestHandler[] {
    const analyticsPassthrough = buildAnalyticsPassthroughHandler();
    const memberTasks = buildMemberTaskHandlers(profile, resolveStateCode(profile.state));
    const overrides = buildOverrideHandlers(profile);
    const questionnaire = buildQuestionnaireHandlers();
    const payroll = buildPayrollHandlers(profile);
    const baseline = Object.values(storybookHandlerMap) as RequestHandler[];
    const fallbacks = buildFallbackHandlers();
    const catchAll = buildCatchAllHandler();
    return [
        analyticsPassthrough,
        ...memberTasks,
        ...overrides,
        ...questionnaire,
        ...payroll,
        ...baseline,
        ...fallbacks,
        catchAll,
    ];
}
